import { MantineProvider, createTheme, type MantineColorsTuple } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import { SpotlightProvider } from './SpotlightProvider';
import { AuthProvider } from './AuthProvider';
import { AbilityProvider } from './AbilityProvider';
import { QueryProvider } from './QueryProvider';
import type { ReactNode } from 'react';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/tiptap/styles.css';
import 'mantine-react-table/styles.css';
import '@/shared/styles/safe-area.css';
import '@/shared/styles/app-shell.css';

const brand: MantineColorsTuple = [
  '#e7f5ff',
  '#d0ebff',
  '#a5d8ff',
  '#74c0fc',
  '#4dabf7',
  '#339af0',
  '#228be6',
  '#1c7ed6',
  '#1971c2',
  '#1864ab',
];

const theme = createTheme({
  primaryColor: 'brand',
  colors: { brand },
  defaultRadius: 'lg',
  fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '600',
  },
  // Ombre più morbide e profonde dei default Mantine: danno "profondità"
  // alle superfici senza appesantire (look meno piatto).
  shadows: {
    xs: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
    sm: '0 2px 6px -2px rgba(15, 23, 42, 0.06), 0 4px 12px -4px rgba(15, 23, 42, 0.10)',
    md: '0 6px 18px -6px rgba(15, 23, 42, 0.12), 0 2px 6px -2px rgba(15, 23, 42, 0.06)',
    lg: '0 18px 40px -16px rgba(15, 23, 42, 0.18), 0 4px 12px -6px rgba(15, 23, 42, 0.08)',
    xl: '0 28px 60px -20px rgba(15, 23, 42, 0.22)',
  },
  cursorType: 'pointer',
  components: {
    Card: {
      defaultProps: { shadow: 'sm', withBorder: true, radius: 'lg' },
    },
    Paper: {
      defaultProps: { radius: 'lg' },
    },
    Button: {
      defaultProps: { radius: 'md' },
    },
  },
});

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <DatesProvider settings={{ locale: 'it', firstDayOfWeek: 1, weekendDays: [0, 6] }}>
        <Notifications position="top-right" />
        <QueryProvider>
          <AuthProvider>
            <AbilityProvider>
              <ModalsProvider>
                <SpotlightProvider>{children}</SpotlightProvider>
              </ModalsProvider>
            </AbilityProvider>
          </AuthProvider>
        </QueryProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
